import React from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { useGetMyMembershipsQuery } from "api/account/accountApiSlice";
import { AccountApiError, MembershipClaim } from "api/account/types";

/**
 * The organizations this account belongs to (H-3b).
 *
 * READ-ONLY, and deliberately so. Until 2026-08-20 this section also offered a
 * picker of the tenant's organizations plus an "apply" button (H-3c, #1800).
 * That picker was the tenant's CUSTOMER LIST, readable by anyone who could
 * create an account — and account signup is self-service. It was removed
 * entirely rather than hidden; see ADR-0021.
 *
 * **Membership now arrives by invitation only.** The organization adds the
 * address, the person gets an invite mail, and registering with that address
 * turns the invite into a membership. Nothing in the app asks to join.
 *
 * `pending` and `rejected` are still rendered even though nothing can produce
 * them any more: rows created before the removal still exist, and a row that
 * rendered as blank would be worse than one that explains itself.
 */
const MembershipsSection: React.FC = () => {
  const { t } = useTranslation();
  // Re-asked every time the page opens. The entry is argument-less and
  // nothing invalidates `MyMemberships`, so without this an answer cached
  // earlier in the session — before an approval, or for a session that has
  // since expired — would be served as the current one.
  const {
    data: claims,
    isFetching,
    isError,
    isSuccess,
    error,
  } = useGetMyMembershipsQuery(undefined, { refetchOnMountOrArgChange: true });

  // `Array.isArray`, not `?? []` — an error body or an unexpected shape is
  // truthy and would sail past the fallback, and `.filter` on it takes the
  // whole account page down. House rule after the `pricing_discounts = {}`
  // incidents: shape-check, never trust.
  const claimRows: MembershipClaim[] = Array.isArray(claims) ? claims : [];

  // A removed membership is not an outcome worth showing — it reads as a dead
  // entry the person cannot act on, and there is no longer a way back in from
  // here anyway.
  const visibleRows = claimRows.filter(
    (row) => row.status?.toLowerCase() !== "suspended"
  );

  // A failure must never look like an absence. Three states are NOT "you
  // belong to nothing": the request failed, it answered with something that
  // is not a list, or it has not answered yet. Only a settled, well-formed
  // empty list is. A refetch over a cached answer counts as "not answered
  // yet" too — whatever was cached is exactly what may be stale (or belong to
  // a session that has since ended), so it is not shown in the meantime.
  const malformed = isSuccess && !isFetching && !Array.isArray(claims);
  const failed = !isFetching && (isError || malformed);
  const loading = !failed && (isFetching || !isSuccess);
  const rawServerMessage = isError
    ? (error as AccountApiError | undefined)?.data?.message
    : undefined;
  const serverMessage =
    typeof rawServerMessage === "string" ? rawServerMessage : undefined;

  /**
   * The contact-person mark replaces "Member" rather than being appended to
   * it: the heading above already says "You are a member of", so spelling it
   * out again on the row and then adding the stronger fact reads as two
   * labels for one thing. Being a member is implicit in being the contact
   * (PO, 2026-08-26).
   *
   * Only on an APPROVED row — a pending or rejected claim has to keep saying
   * what happened to it, and the mark would bury that.
   */
  const statusLine = (row: MembershipClaim) => {
    const status = row.status?.toLowerCase();
    if (status === "pending") return t("account.memberships.state.pending");
    if (status === "approved") {
      return row.isContact
        ? t("account.memberships.state.contact")
        : t("account.memberships.state.approved");
    }
    if (status === "rejected") return t("account.memberships.state.rejected");
    return t("account.memberships.state.other");
  };

  const renderBody = () => {
    if (failed) return (
      <div role="alert" className="space-y-1">
        <p className="text-sm text-foreground">
          {t("account.memberships.load_error")}
        </p>
        {/* The server's own words, so the cause is on screen rather than
            needing devtools (#2255). */}
        {serverMessage && (
          <p className="text-xs text-slate-500">{serverMessage}</p>
        )}
      </div>
    );
    if (loading) return (
      <p className="flex items-center gap-2 text-sm text-slate-600">
        <Loader2 className="h-4 w-4 animate-spin" />
        {t("common.loading")}
      </p>
    );
    if (visibleRows.length > 0) return (
      <ul className="space-y-2">
        {visibleRows.map((row) => (
          <li key={row.id} className="rounded-md bg-primary/5 px-3 py-2">
            <p className="text-sm text-foreground">{row.organizationName}</p>
            <p className="text-sm text-slate-600">{statusLine(row)}</p>
            {row.status?.toLowerCase() === "pending" && (
              <p className="text-xs text-slate-500">
                {t("account.memberships.pending_consequence")}
              </p>
            )}
            {row.status?.toLowerCase() === "rejected" && row.rejectReason && (
              <p className="text-xs text-slate-500">
                {t("account.memberships.reject_reason", {
                  reason: row.rejectReason,
                })}
              </p>
            )}
          </li>
        ))}
      </ul>
    );
    return (
      <p className="text-sm text-slate-600">
        {t("account.memberships.none")}
      </p>
    );
  };

  return (
    <div className="space-y-3">
      <h2 className="text-sm font-medium text-slate-700">
        {t("account.memberships.title")}
      </h2>

      {/* "Du bist Mitglied von:" — phrased and rendered as a list from the
          start, because an account can hold several memberships and a
          single-value layout would have to be rebuilt the first time one does
          (PO, 2026-08-20). */}
      {!failed && !loading && visibleRows.length > 0 && (
        <p className="text-sm text-slate-600">
          {t("account.memberships.member_of")}
        </p>
      )}

      {renderBody()}

      {/* Says how membership actually happens now, so an empty list is not a
          dead end the person has no answer to. */}
      <p className="text-sm text-slate-600">
        {t("account.memberships.invite_only_hint")}
      </p>
    </div>
  );
};

export default MembershipsSection;
